import React, { useState } from 'react';
import '../ShipturtleProducts/ManageProducts.css';
import './ManageOrders.css';

function OrderFilters({ filters, onFilterChange, isReadOnly }) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  
  const handleInputChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
    
    if (field === 'dateRange') {
      setShowCustomDateRange(value === 'custom');
    }
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalFilters(prev => ({ ...prev, globalSearch: value }));
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      onFilterChange({ ...localFilters, globalSearch: value });
    }, 300);
  };
  
  const clearFilters = () => {
    const clearedFilters = {
      globalSearch: '',
      orderStatus: 'all',
      paymentStatus: 'all',
      payoutStatus: 'all',
      dateRange: 'all'
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };
  
  const hasActiveFilters = localFilters.globalSearch || 
                          localFilters.orderStatus !== 'all' || 
                          localFilters.paymentStatus !== 'all' ||
                          localFilters.payoutStatus !== 'all' ||
                          localFilters.dateRange !== 'all';
  
  return (
    <div className="filtersContainer">
      <div className="filtersHeader">
        <h3>Filters</h3>
        {hasActiveFilters && (
          <button 
            onClick={clearFilters}
            className="clearButton"
            disabled={isReadOnly}
          >
            Clear All
          </button>
        )}
      </div>
      
      <div className="filtersGrid">
        {/* Global Search Input */}
        <div className="filterGroup filterGroupWide">
          <label htmlFor="globalSearch" className="filterLabel">
            Search Orders
          </label>
          <input
            id="globalSearch"
            type="text"
            placeholder="Search by order number, customer name, or vendor..."
            value={localFilters.globalSearch || ''}
            onChange={handleSearchChange}
            className="searchInput"
            disabled={isReadOnly}
          />
        </div>
        
        {/* Order Status Filter */}
        <div className="filterGroup">
          <label htmlFor="orderStatus" className="filterLabel">
            Order Status
          </label>
          <select
            id="orderStatus"
            value={localFilters.orderStatus || 'all'}
            onChange={(e) => handleInputChange('orderStatus', e.target.value)}
            className="selectInput"
            disabled={isReadOnly}
          >
            <option value="all">All Statuses</option>
            <option value="New Orders">New Orders</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Ready to Ship">Ready to Ship</option>
            <option value="In Transit">In Transit</option>
            <option value="Exceptions">Exceptions</option>
            <option value="Delivered">Delivered</option>
            <option value="RTO">RTO</option>
            <option value="Held">Held</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        
        {/* Payment Status Filter */}
        <div className="filterGroup">
          <label htmlFor="paymentStatus" className="filterLabel">
            Payment Status
          </label>
          <select
            id="paymentStatus"
            value={localFilters.paymentStatus || 'all'}
            onChange={(e) => handleInputChange('paymentStatus', e.target.value)}
            className="selectInput"
            disabled={isReadOnly}
          >
            <option value="all">All Payment Status</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PENDING">Pending</option>
          </select>
        </div>
        
        {/* Payout Status Filter */}
        <div className="filterGroup">
          <label htmlFor="payoutStatus" className="filterLabel">
            Payout Status
          </label>
          <select
            id="payoutStatus"
            value={localFilters.payoutStatus || 'all'}
            onChange={(e) => handleInputChange('payoutStatus', e.target.value)}
            className="selectInput"
            disabled={isReadOnly}
          >
            <option value="all">All Payout Status</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PROCESSING">Processing</option>
          </select>
        </div>
        
        {/* Date Range Filter */}
        <div className="filterGroup filterGroupDateRange">
          <label htmlFor="dateRange" className="filterLabel">
            Date Range
          </label>
          <select
            id="dateRange"
            value={localFilters.dateRange || 'all'}
            onChange={(e) => handleInputChange('dateRange', e.target.value)}
            className="selectInput"
            disabled={isReadOnly}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
            <option value="custom">Custom Range</option>
          </select>
          {showCustomDateRange && (
            <div className="customDateRange">
              <div className="dateInputGroup">
                <label htmlFor="startDate">From:</label>
                <input
                  type="date"
                  id="startDate"
                  className="dateInput"
                  disabled={isReadOnly}
                />
              </div>
              <div className="dateInputGroup">
                <label htmlFor="endDate">To:</label>
                <input
                  type="date"
                  id="endDate"
                  className="dateInput"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="activeFilters">
          <span className="activeFiltersLabel">Active Filters:</span>
          <div className="filterTags">
            {localFilters.globalSearch && (
              <span className="filterTag">
                Search: "{localFilters.globalSearch}"
                <button 
                  onClick={() => handleInputChange('globalSearch', '')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.orderStatus !== 'all' && (
              <span className="filterTag">
                Status: {localFilters.orderStatus}
                <button 
                  onClick={() => handleInputChange('orderStatus', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.paymentStatus !== 'all' && (
              <span className="filterTag">
                Payment: {localFilters.paymentStatus}
                <button 
                  onClick={() => handleInputChange('paymentStatus', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.payoutStatus !== 'all' && (
              <span className="filterTag">
                Payout: {localFilters.payoutStatus}
                <button 
                  onClick={() => handleInputChange('payoutStatus', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.dateRange !== 'all' && (
              <span className="filterTag">
                Date: {localFilters.dateRange}
                <button 
                  onClick={() => handleInputChange('dateRange', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderFilters;